"use client";

// ============================================================
// CLUBHUB – Mein Bereich: Familie und Zugaenge
// ============================================================
// Wer verwaltet wen — offen gezeigt. Ein Zugang (eine Mailadresse)
// verwaltet die eigene Person, Kinder (sorgeberechtigt) und
// Personen im Haushalt; ein Kind kann zwei Zugaenge haben (beide
// Elternteile). Mit 18 endet die Verwaltung eines Kindes von
// selbst.
//
// BACKEND: ClubUserContact (clubUserId, contactId, relation);
// Einladen eines zweiten Elternteils → Invitation mit Rolle MEMBER
// und vorbelegter Zuordnung; Hinzufuegen einer bestehenden Person
// nur nach Pruefung durch den Vorstand (Nummer + Geburtsdatum).
// ============================================================
import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ArrowRight, Info, KeyRound, Link2, UserPlus, UsersRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogCancel, DialogHeader, DialogSubmit, DialogTitle, DialogNotice } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { confirmDialog } from "@/components/ui/confirm";
import { toast } from "@/components/ui/toast";
import { TonBadge } from "@/components/mitglieder/status-badge";
import { CONTACT_STATUS } from "@/lib/labels";
import { formatDate } from "@/lib/utils";
import { nochNichtHier, useEcht } from "./quelle";
import { emailGueltig } from "@/lib/formulare/pruefen";
import { personSetzen } from "@/lib/portal/person";
import type { PortalLogin, PortalPerson } from "@/lib/portal/views";
import { PersonKreis, beziehungText } from "./shell";

function volljaehrigAm(geburt: string | null): string | null {
  if (!geburt) return null;
  const [y, m, d] = geburt.split("-").map(Number) as [number, number, number];
  return `${y + 18}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function EinladenDialog({ kind, offen, onClose }: { kind: PortalPerson; offen: boolean; onClose: () => void }) {
  const echt = useEcht();
  const [mail, setMail] = useState("");
  const [fehler, setFehler] = useState<string | undefined>();
  function senden(e: React.FormEvent) {
    e.preventDefault();
    if (!emailGueltig(mail)) {
      setFehler("Bitte eine vollständige Mailadresse angeben.");
      return;
    }
    // BACKEND: Invitation (Rolle MEMBER) mit vorbelegter Zuordnung GUARDIAN auf das Kind.
    if (echt) {
      nochNichtHier("Eine Einladung");
      return onClose();
    }
    toast.success("Einladung verschickt", { description: `${mail} bekommt einen Anmeldelink und verwaltet ${kind.firstName} danach mit.` });
    setMail("");
    onClose();
  }
  return (
    <Dialog open={offen} onClose={onClose}>
      <form onSubmit={senden} className="flex min-h-0 flex-1 flex-col" noValidate>
        <DialogHeader>
          <DialogTitle>Zweite sorgeberechtigte Person einladen</DialogTitle>
          <DialogDescription>Sie bekommt einen eigenen Zugang mit eigener Anmeldung — kein geteiltes Kennwort.</DialogDescription>
        </DialogHeader>
        <DialogContent>
          <Input id="einladen-mail" type="email" label="Mailadresse" value={mail} onChange={(e) => { setMail(e.target.value); setFehler(undefined); }} error={fehler} autoComplete="email" />
          <DialogNotice tone="neutral" icon={<Info className="text-text-tertiary h-4 w-4" aria-hidden />}>
            Sie sieht Termine, Beiträge und Einwilligungen von {kind.firstName}, aber nicht deine eigenen Angaben.
          </DialogNotice>
        </DialogContent>
        <DialogFooter>
          <DialogCancel />
          <DialogSubmit>Einladen</DialogSubmit>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

function VerknuepfenDialog({ offen, onClose }: { offen: boolean; onClose: () => void }) {
  const echt = useEcht();
  const [nr, setNr] = useState("");
  const [geb, setGeb] = useState("");
  const [fehler, setFehler] = useState<{ nr?: string; geb?: string }>({});
  function senden(e: React.FormEvent) {
    e.preventDefault();
    const f: typeof fehler = {};
    if (!/^\d{1,6}$/.test(nr.trim())) f.nr = "Die Mitgliedsnummer steht auf dem Ausweis oder der Beitragsrechnung.";
    if (!geb) f.geb = "Bitte das Geburtsdatum angeben.";
    setFehler(f);
    if (Object.keys(f).length) return;
    // BACKEND: Anfrage an den Vorstand; erst nach Freigabe entsteht ClubUserContact.
    if (echt) {
      nochNichtHier("Eine Verknüpfung");
      return onClose();
    }
    toast.success("Anfrage gestellt", { description: "Der Vorstand prüft die Angaben und schaltet die Person frei. Du bekommst eine Mail." });
    setNr("");
    setGeb("");
    onClose();
  }
  return (
    <Dialog open={offen} onClose={onClose}>
      <form onSubmit={senden} className="flex min-h-0 flex-1 flex-col" noValidate>
        <DialogHeader>
          <DialogTitle>Bestehendes Mitglied hinzufügen</DialogTitle>
          <DialogDescription>Für Kinder oder Angehörige, die schon Mitglied sind. Der Vorstand bestätigt die Zuordnung.</DialogDescription>
        </DialogHeader>
        <DialogContent>
          <Input id="verkn-nr" label="Mitgliedsnummer" value={nr} onChange={(e) => setNr(e.target.value)} error={fehler.nr} inputMode="numeric" className="tabular-nums" />
          <Input id="verkn-geb" type="date" label="Geburtsdatum" value={geb} onChange={(e) => setGeb(e.target.value)} error={fehler.geb} />
        </DialogContent>
        <DialogFooter>
          <DialogCancel />
          <DialogSubmit>Anfrage stellen</DialogSubmit>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

export function Familie({ persons, activeId, login, vereinSlug }: { persons: PortalPerson[]; activeId: string; login: PortalLogin; vereinSlug: string }) {
  const router = useRouter();
  const echt = useEcht();
  const [einladenFuer, setEinladenFuer] = useState<PortalPerson | null>(null);
  const [verknuepfen, setVerknuepfen] = useState(false);
  const [liste, setListe] = useState(persons);

  function ansehen(p: PortalPerson) {
    personSetzen(p.contactId);
    router.push("/mein-bereich/profil");
    router.refresh();
  }

  async function beenden(p: PortalPerson) {
    const ja = await confirmDialog({
      title: `${p.firstName} nicht mehr mitverwalten?`,
      description: `Du siehst danach Termine, Beiträge und Angaben von ${p.firstName} nicht mehr. Die Mitgliedschaft bleibt bestehen; der Vorstand schreibt ${p.firstName} künftig direkt${p.email ? "" : " per Post"}.`,
      confirmLabel: "Verwaltung beenden",
      danger: true,
    });
    if (!ja) return;
    // BACKEND: ClubUserContact loeschen (nur HOUSEHOLD; GUARDIAN endet ueber den Vorstand oder mit 18).
    if (echt) return nochNichtHier("Die Verwaltung beenden");
    setListe((l) => l.filter((x) => x.contactId !== p.contactId));
    toast("Verwaltung beendet", { description: `${p.firstName} ist aus deinem Zugang entfernt.` });
  }

  return (
    <div className="space-y-5">
      <Card>
        <CardContent className="flex items-start gap-3 p-5">
          <KeyRound className="text-text-tertiary mt-0.5 h-5 w-5 shrink-0" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-body-md text-text-primary font-medium">
              Dein Zugang <span className="text-text-secondary font-normal">{login.email}</span>
            </p>
            <p className="text-body-sm text-text-secondary mt-1">
              Du meldest dich einmal an und verwaltest {liste.length === 1 ? "dich selbst" : `${liste.length} Personen`}. Oben rechts wählst du, für wen du gerade schaust — Termine beantwortest du auf dem Start für alle auf einmal.
            </p>
          </div>
        </CardContent>
      </Card>

      <ul className="space-y-3">
        {liste.map((p) => {
          const aktiv = p.contactId === activeId;
          const minderjaehrig = p.age !== null && p.age < 18;
          const mit18 = minderjaehrig ? volljaehrigAm(p.birthDate) : null;
          return (
            <li key={p.contactId} className="bg-bg-elevated shadow-e2 rounded-2xl ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
              <div className="flex items-start gap-3.5 p-4">
                <PersonKreis person={p} groesse="lg" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <h2 className="text-heading-sm text-text-primary">{p.name}</h2>
                    {aktiv && <TonBadge ton="accent">Gerade gewählt</TonBadge>}
                  </div>
                  <p className="text-body-sm text-text-secondary">{beziehungText(p)}</p>
                  <p className="text-caption text-text-tertiary mt-1 flex flex-wrap gap-x-2 tabular-nums">
                    <span>{CONTACT_STATUS[p.status].label}</span>
                    {p.memberNumber && <span>· Nr. {p.memberNumber}</span>}
                    {p.groups.length > 0 && <span>· {p.groups.map((g) => g.name).join(", ")}</span>}
                  </p>
                </div>
                <Button type="button" variant={aktiv ? "ghost" : "secondary"} size="sm" onClick={() => ansehen(p)}>
                  Ansehen
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                </Button>
              </div>
              {p.relation !== "SELF" && (
                <div className="border-border-subtle text-body-sm space-y-2 border-t px-4 py-3">
                  {p.relation === "GUARDIAN" && (
                    <>
                      <p className="text-text-secondary flex items-start gap-2">
                        <UsersRound className="text-text-tertiary mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                        {p.alsoManagedBy.length > 0 ? `Auch verwaltet von ${p.alsoManagedBy.join(", ")} (eigener Zugang).` : "Nur von dir verwaltet."}
                      </p>
                      {mit18 && (
                        <p className="text-text-secondary flex items-start gap-2">
                          <Info className="text-text-tertiary mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                          <span>
                            Ab dem 18. Geburtstag am <span className="tabular-nums">{formatDate(mit18)}</span> verwaltet sich {p.firstName} selbst; deine Verwaltung endet dann von allein.
                          </span>
                        </p>
                      )}
                      {p.alsoManagedBy.length === 0 && (
                        <Button type="button" variant="ghost" size="sm" className="-ml-3" onClick={() => setEinladenFuer(p)}>
                          <UserPlus className="h-3.5 w-3.5" aria-hidden />
                          Zweite sorgeberechtigte Person einladen
                        </Button>
                      )}
                    </>
                  )}
                  {p.relation === "HOUSEHOLD" && (
                    <>
                      <p className="text-text-secondary flex items-start gap-2">
                        <Info className="text-text-tertiary mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                        {p.ownLogin ? `${p.firstName} hat zusätzlich einen eigenen Zugang.` : `${p.firstName} hat keinen eigenen Zugang${p.email ? "" : " und keine eigene Mailadresse"} — Nachrichten an ${p.firstName} kommen bei dir an.`}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        <Button type="button" variant="ghost" size="sm" className="-ml-3" onClick={() => (echt ? nochNichtHier("Ein eigener Zugang") : toast.success(`Einladung für ${p.firstName} vorbereitet`, { description: "Der Vorstand schickt einen Anmeldelink — per Mail oder als Brief." }))}>
                          <UserPlus className="h-3.5 w-3.5" aria-hidden />
                          Eigenen Zugang anlegen
                        </Button>
                        <Button type="button" variant="ghost" size="sm" className="text-text-secondary" onClick={() => beenden(p)}>
                          Nicht mehr mitverwalten
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Link
          href={`/f/${vereinSlug}/aufnahme?von=mitgliederbereich`}
          className="border-border hover:bg-bg-primary flex items-start gap-3 rounded-2xl border border-dashed p-4 transition-colors"
        >
          <UserPlus className="text-text-tertiary mt-0.5 h-5 w-5 shrink-0" aria-hidden />
          <span>
            <span className="text-body-md text-text-primary block font-medium">Weitere Person anmelden</span>
            <span className="text-caption text-text-tertiary block">Aufnahmeantrag für ein Kind oder Angehörige — Familienbeitrag wird geprüft.</span>
          </span>
        </Link>
        <button type="button" onClick={() => setVerknuepfen(true)} className="border-border hover:bg-bg-primary flex items-start gap-3 rounded-2xl border border-dashed p-4 text-left transition-colors">
          <Link2 className="text-text-tertiary mt-0.5 h-5 w-5 shrink-0" aria-hidden />
          <span>
            <span className="text-body-md text-text-primary block font-medium">Bestehendes Mitglied hinzufügen</span>
            <span className="text-caption text-text-tertiary block">Schon Mitglied, aber noch nicht in deinem Zugang? Mit Nummer und Geburtsdatum.</span>
          </span>
        </button>
      </div>

      {einladenFuer && <EinladenDialog kind={einladenFuer} offen onClose={() => setEinladenFuer(null)} />}
      <VerknuepfenDialog offen={verknuepfen} onClose={() => setVerknuepfen(false)} />
    </div>
  );
}
